// Phase 44.31.10 — Admin · Scheda Cliente dettagliata
// Vista completa cliente: anagrafica, wallet, ordini, sessioni, audit log.
// Stesso pattern di DriverDetail (lettura) + CTA "Modifica" che apre AdminUserEditDrawer.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Mail, Phone, Building2, Wallet, Package, Clock,
  Monitor, ShieldCheck, FileSearch, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime, ROLE_LABEL } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import AdminUserEditDrawer from "@/components/admin/AdminUserEditDrawer";

const ROLE_PILL_CLS = {
  customer_private:  "bg-blue-50 text-blue-700",
  customer_business: "bg-violet-50 text-violet-700",
  customer_shop:     "bg-amber-50 text-amber-700",
};

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/${id}/detail`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-dapnow-muted">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-4">
        <Link to="/admin/users?role=customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
          <ArrowLeft className="h-4 w-4" /> Clienti
        </Link>
        <p className="text-dapnow-red text-sm">Cliente non trovato.</p>
      </div>
    );
  }

  const u = data.user;
  const w = data.wallet || {};
  const o = data.orders || {};

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="admin-customer-detail-page">
      <Link to="/admin/users?role=customers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Clienti
      </Link>

      {/* Header */}
      <section className="card-soft p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`pill ${ROLE_PILL_CLS[u.role] || "bg-zinc-50 text-zinc-700"}`}>
                {ROLE_LABEL[u.role] || u.role}
              </span>
              {u.customer_number && (
                <span className="pill bg-zinc-100 text-dapnow-ink font-mono text-[10px]" data-testid="customer-number">
                  {u.customer_number}
                </span>
              )}
              {u.account_status === "suspended" && (
                <span className="pill bg-red-50 text-red-700">Sospeso</span>
              )}
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-2" data-testid="customer-detail-name">
              {u.business_name || u.name || "—"}
            </h1>
            {u.business_name && u.name && (
              <p className="text-sm text-dapnow-muted mt-0.5">Referente: <span className="text-dapnow-ink font-semibold">{u.name}</span></p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-dapnow-muted">
              <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {u.email}</span>
              {u.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {u.phone_prefix || ""} {u.phone}</span>}
              {u.business_vat && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> P.IVA {u.business_vat}</span>}
            </div>
          </div>
          <button
            onClick={() => setEditOpen(true)}
            data-testid="admin-customer-edit-btn"
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-dapnow-ink text-white text-sm font-bold hover:opacity-90"
          >
            <Pencil className="h-4 w-4" /> Modifica
          </button>
        </div>
      </section>

      {/* Stat tiles */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="customer-stats">
        <Stat icon={Wallet} label="Saldo wallet" value={formatCHF(w.credit_balance_chf || 0)} accent="amber" />
        <Stat icon={Package} label="Pack rimanenti" value={String(w.delivery_packs_remaining || 0)} accent="violet" />
        <Stat icon={ShieldCheck} label="Ordini totali" value={String(o.total || 0)} accent="blue" />
        <Stat icon={Wallet} label="Spesa totale" value={formatCHF(o.total_spent_chf || 0)} accent="emerald" />
      </section>

      {/* Subscription */}
      {w.subscription && (
        <section className="card-soft p-4 sm:p-5">
          <p className="overline">Abbonamento</p>
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-display text-lg font-bold tracking-tight">{w.subscription.plan_id || "—"}</p>
              <p className="text-xs text-dapnow-muted">
                Stato: <strong className="text-dapnow-ink">{w.subscription.status || "—"}</strong>
                {w.subscription.current_period_end && (
                  <> · Periodo fino al <strong className="text-dapnow-ink">{formatDateTime(w.subscription.current_period_end)}</strong></>
                )}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Indirizzo principale */}
      {u.primary_address && (
        <section className="card-soft p-4 sm:p-5">
          <p className="overline">Indirizzo principale</p>
          <p className="mt-2 text-sm text-dapnow-ink">
            {[u.primary_address.street, u.primary_address.house_number].filter(Boolean).join(" ")},{" "}
            {[u.primary_address.zip, u.primary_address.city].filter(Boolean).join(" ")}
            {u.primary_address.canton ? `, ${u.primary_address.canton}` : ""}
          </p>
        </section>
      )}

      {/* Recent orders */}
      <section className="card-soft p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="overline">Ultimi 20 ordini</p>
          <Link to={`/admin/orders?customer_id=${u.id}`} className="text-xs font-semibold text-dapnow-blue hover:underline">
            Vedi tutti →
          </Link>
        </div>
        {o.recent && o.recent.length > 0 ? (
          <ul className="divide-y divide-dapnow-line" data-testid="customer-orders-list">
            {o.recent.map((order) => (
              <li key={order.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/admin/orders/${order.id}`} data-testid={`customer-order-link-${order.id}`}
                      className="font-mono text-xs font-semibold text-dapnow-blue hover:underline">
                      {order.id.slice(0, 8)}
                    </Link>
                    <StatusBadge status={order.status} />
                    {order.delivery_type && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-dapnow-muted">{order.delivery_type}</span>
                    )}
                  </div>
                  <p className="text-xs text-dapnow-muted truncate mt-0.5">
                    {order.pickup_address?.split(",")[0]} → {order.delivery_address?.split(",")[0]}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-sm">{formatCHF(order.final_price ?? order.price ?? 0)}</p>
                  <p className="text-[10px] text-dapnow-muted">{formatDateTime(order.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-dapnow-muted">Nessun ordine ancora.</p>
        )}
      </section>

      {/* Active sessions */}
      <section className="card-soft p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="overline inline-flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Sessioni attive</p>
          <Link to={`/admin/users/${u.id}/sessions`} className="text-xs font-semibold text-dapnow-blue hover:underline">
            Gestisci →
          </Link>
        </div>
        {data.sessions_active && data.sessions_active.length > 0 ? (
          <ul className="space-y-2 text-xs" data-testid="customer-sessions-list">
            {data.sessions_active.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-dapnow-line bg-white px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-dapnow-ink">{s.device_label || "Dispositivo sconosciuto"}</p>
                  <p className="text-dapnow-muted font-mono text-[10px]">{s.ip || "—"}</p>
                </div>
                <span className="text-[10px] text-dapnow-muted shrink-0 inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {formatDateTime(s.last_seen_at)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-dapnow-muted">Nessuna sessione attiva.</p>
        )}
      </section>

      {/* Audit recent */}
      {data.audit_recent && data.audit_recent.length > 0 && (
        <section className="card-soft p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="overline inline-flex items-center gap-1.5"><FileSearch className="h-3.5 w-3.5" /> Modifiche recenti</p>
            <Link to={`/admin/audit-log?target=${u.id}`} className="text-xs font-semibold text-dapnow-blue hover:underline">
              Vedi tutto →
            </Link>
          </div>
          <ul className="space-y-2 text-xs" data-testid="customer-audit-list">
            {data.audit_recent.map((a) => (
              <li key={a.id} className="rounded-lg bg-zinc-50 px-3 py-2">
                <p className="font-semibold text-dapnow-ink">{a.field || a.action || "Modifica"}</p>
                <p className="text-dapnow-muted text-[11px] mt-0.5">
                  {a.admin_name || a.actor_id} · {formatDateTime(a.created_at)}
                  {a.reason && <> · <em>"{a.reason}"</em></>}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {editOpen && (
        <AdminUserEditDrawer
          user={u}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue:    "border-blue-200 bg-blue-50 text-blue-700",
    violet:  "border-violet-200 bg-violet-50 text-violet-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-2xl border p-4 ${accents[accent] || accents.blue}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <p className="overline text-[10px] font-bold">{label}</p>
      </div>
      <p className="font-display text-xl sm:text-2xl font-extrabold">{value}</p>
    </div>
  );
}
